import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';
import { apiClient } from '@/utils/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { handleSecureError, secureLog } from '@/utils/secureErrorHandler';
import { SessionRecord } from '@/types/session';
import { sessionHistoryKeys } from '../queries/sessionHistoryKeys';

export const useSessionMutations = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Track which specific session is being deleted (for per-item loading states)
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

  // Toggle favorite mutation - uses PATCH /sessions/{id}
  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({ sessionId, sessions }: { sessionId: string; sessions: SessionRecord[] }) => {
      if (!user) throw new Error('User not authenticated');

      const session = sessions.find(s => s.id === sessionId);
      if (!session) throw new Error('Session not found');

      const newFavoriteStatus = !session.is_favorite;

      await apiClient.patch(`/sessions/${sessionId}`, {
        is_favorite: newFavoriteStatus
      });

      return { sessionId, newFavoriteStatus };
    },
    onSuccess: ({ newFavoriteStatus }) => {
      // Invalidate all session queries to refetch
      queryClient.invalidateQueries({ queryKey: sessionHistoryKeys.all });

      toast({
        title: newFavoriteStatus ? "Added to Favorites" : "Removed from Favorites",
        description: newFavoriteStatus
          ? "Session added to your favorites."
          : "Session removed from your favorites.",
      });
    },
    onError: (error) => {
      const secureError = handleSecureError(error, 'sessions_favorite');
      toast({
        title: "Error",
        description: secureError.userMessage,
        variant: "destructive",
      });
    }
  });

  // Delete session mutation - uses DELETE /sessions/{id}
  const deleteSessionMutation = useMutation({
    mutationFn: async ({ sessionId }: { sessionId: string }) => {
      if (!user) throw new Error('User not authenticated');

      // Validate sessionId format (UUID)
      if (!sessionId || typeof sessionId !== 'string' || sessionId.length < 10) {
        throw new Error('Invalid session ID');
      }

      await apiClient.delete(`/sessions/${sessionId}`);

      return { sessionId };
    },
    onMutate: ({ sessionId }) => {
      setDeletingSessionId(sessionId);
      return { sessionId };
    },
    onSuccess: ({ sessionId }) => {
      setDeletingSessionId(null);

      // Invalidate React Query cache - triggers automatic refetch
      queryClient.invalidateQueries({ queryKey: sessionHistoryKeys.all });

      toast({
        title: "Session Deleted",
        description: "Session has been removed from your history.",
      });

      secureLog('info', 'Session deleted successfully', { sessionId });
    },
    onError: (error, { sessionId }) => {
      setDeletingSessionId(null);

      const secureError = handleSecureError(error, 'sessions_delete');
      toast({
        title: "Delete Failed",
        description: secureError.userMessage || "Failed to delete session. Please try again.",
        variant: "destructive",
      });

      secureLog('error', 'Session delete failed', { sessionId, error: secureError });
    },
    onSettled: () => {
      setDeletingSessionId(null);
    }
  });

  // Track sessions being deleted (for bulk operations)
  const [deletingSessionIds, setDeletingSessionIds] = useState<Set<string>>(new Set());

  // Wrapper function with confirmation
  const deleteSessionWithConfirmation = useCallback(
    (sessionId: string, sessions: SessionRecord[], skipConfirmation = false) => {
      // Prevent double-deletion of the same session
      if (deletingSessionId === sessionId || deletingSessionIds.has(sessionId)) {
        return;
      }

      // For single deletes (with confirmation), prevent if another single delete is in progress
      if (!skipConfirmation && deleteSessionMutation.isPending) {
        toast({
          title: "Please wait",
          description: "Another deletion is in progress.",
          variant: "default",
        });
        return;
      }

      const session = sessions.find(s => s.id === sessionId);
      if (!session) {
        return;
      }

      if (!skipConfirmation) {
        const confirmed = window.confirm(
          `Are you sure you want to delete "${session.title || 'this session'}"? This action cannot be undone.`
        );
        if (!confirmed) return;
      }

      deleteSessionMutation.mutate({ sessionId });
    },
    [deleteSessionMutation, deletingSessionId, deletingSessionIds, toast]
  );

  // Bulk delete function - deletes multiple sessions sequentially
  const deleteSessionsBulk = useCallback(
    async (sessionIds: string[]) => {
      if (sessionIds.length === 0) return;

      // Confirm once for all
      const confirmed = window.confirm(
        `Are you sure you want to delete ${sessionIds.length} session${sessionIds.length > 1 ? 's' : ''}? This action cannot be undone.`
      );
      if (!confirmed) return;

      // Track all sessions being deleted
      setDeletingSessionIds(new Set(sessionIds));

      // Delete sessions one by one
      let successCount = 0;
      let failCount = 0;

      for (const sessionId of sessionIds) {
        try {
          await apiClient.delete(`/sessions/${sessionId}`);
          successCount++;
        } catch {
          failCount++;
        }
      }

      // Clear tracking
      setDeletingSessionIds(new Set());

      // Invalidate React Query cache - triggers automatic refetch
      queryClient.invalidateQueries({ queryKey: sessionHistoryKeys.all });

      // Show result toast
      if (failCount === 0) {
        toast({
          title: "Sessions Deleted",
          description: `Successfully deleted ${successCount} session${successCount > 1 ? 's' : ''}.`,
        });
      } else if (successCount === 0) {
        toast({
          title: "Delete Failed",
          description: `Failed to delete ${failCount} session${failCount > 1 ? 's' : ''}. Please try again.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Partial Success",
          description: `Deleted ${successCount} session${successCount > 1 ? 's' : ''}, ${failCount} failed.`,
          variant: "default",
        });
      }

      secureLog('info', 'Bulk delete completed', { successCount, failCount });
    },
    [user, queryClient, toast]
  );

  return {
    toggleFavorite: (sessionId: string, sessions: SessionRecord[]) =>
      toggleFavoriteMutation.mutate({ sessionId, sessions }),
    togglingFavorite: toggleFavoriteMutation.isPending,
    // Delete with confirmation dialog (default behavior)
    deleteSession: deleteSessionWithConfirmation,
    // Delete without confirmation (for when already confirmed externally)
    deleteSessionDirect: (sessionId: string, sessions: SessionRecord[]) =>
      deleteSessionWithConfirmation(sessionId, sessions, true),
    // Bulk delete with single confirmation
    deleteSessionsBulk,
    // Loading states
    deletingSession: deleteSessionMutation.isPending || deletingSessionIds.size > 0,
    deletingSessionId, // Which specific session is being deleted (single delete)
    deletingSessionIds, // Which sessions are being deleted (bulk delete)
    // Helper to check if a specific session is being deleted
    isDeleting: (sessionId: string) => deletingSessionId === sessionId || deletingSessionIds.has(sessionId),
  };
};
